import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import AnalyticsService from "../../../../services/AnalyticsService";
import Cards from "../components/Cards";
import Charts from "../components/Charts";
import { format } from "date-fns";
import { getJobTitleLabel, getJobTitleLabelPlural } from "../../../utils/jobTitleLabel";
import UpdateJobTitleModal from "./UpdateJobTitleModal";

function getOrgDashboardCards() {
  const jobLabel = getJobTitleLabel();
  const jobPluralLabel = getJobTitleLabelPlural();
  return [
    { name: "totalRevenue", displayName: "Total Revenue", prefix: "$", tooltip: `Total revenue generated from all completed ${jobPluralLabel.toLowerCase()} in the current period` },
    { name: "averageRevenue", displayName: "Average Revenue", prefix: "$", tooltip: `Average revenue per ${jobLabel.toLowerCase()}, calculated by dividing total revenue by number of completed ${jobPluralLabel.toLowerCase()}` },
    { name: "totalJobs", displayName: `Total ${jobPluralLabel}`, tooltip: `Total number of ${jobPluralLabel.toLowerCase()} created in the system, including all statuses` },
    { name: "completedJobs", displayName: `Completed ${jobPluralLabel}`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} that have been successfully completed and marked as done`, tooltipPosition: "left" },
    { name: "inProgressJobs", displayName: `In Progress ${jobPluralLabel}`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} currently being worked on by employees` },
    { name: "upcomingJobs", displayName: `Upcoming ${jobPluralLabel}`, tooltip: `Number of ${jobPluralLabel.toLowerCase()} scheduled for future dates that haven't started yet` },
    { name: "activeEmployees", displayName: "Active Employees", tooltip: "Number of employees who have logged in and performed actions in the last 30 days" },
    { name: "activeCustomers", displayName: "Active Customers", tooltip: "Number of customers who have booked or inquired about services in the last 30 days", tooltipPosition: "left" }
  ];
}

function getOrgDashboardCharts() {
  const jobLabel = getJobTitleLabel();
  const jobPluralLabel = getJobTitleLabelPlural();
  return [
    { name: "jobStatusDistribution", chartType: "donut", displayName: `${jobLabel} Status Distribution`, subTitle: `Distribution of ${jobPluralLabel.toLowerCase()} by status`, index: "status", categories: ["count"], valueFormatter: (value) => `${value} ${jobPluralLabel.toLowerCase()}` }
  ];
}

const OrganizationDashboard = () => {
  const [cards, setCards] = useState(() => getOrgDashboardCards());
  const [charts, setCharts] = useState(() => getOrgDashboardCharts());
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [jobTitleModalOpen, setJobTitleModalOpen] = useState(false);

  useEffect(() => {
    const handleLabelUpdate = () => {
      setCards(getOrgDashboardCards());
      setCharts(getOrgDashboardCharts());
    };
    window.addEventListener("jobTitleLabelUpdated", handleLabelUpdate);
    return () => window.removeEventListener("jobTitleLabelUpdated", handleLabelUpdate);
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const response = await AnalyticsService.getAdminOrganizationAnalytics();
      if (response.success) {
        setAnalytics(response.data);
      } else {
        throw new Error("Failed to fetch analytics");
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to fetch analytics data");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await AnalyticsService.exportOrganizationAnalytics();
      
      if (response.success) {
        // Create a download link
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `organization-analytics-${format(new Date(), 'yyyy-MM-dd')}.csv`;
        document.body.appendChild(a);
        a.click();
        
        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Organization data exported successfully');
      } else {
        throw new Error(response.message || 'Failed to export organization data');
      }
    } catch (error) {
      console.error('Error exporting organization data:', error);
      toast.error('Failed to export organization data');
    }
  };

  // Card loader component
  const CardLoader = () => (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="animate-pulse">
        <div className="h-4 w-24 bg-gray-200 rounded mb-4"></div>
        <div className="h-8 w-16 bg-gray-300 rounded"></div>
      </div>
    </div>
  );

  // Chart loader component
  const ChartLoader = () => (
    <div className="bg-white rounded-xl shadow-sm">
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-6 w-32 bg-gray-200 rounded mb-2"></div>
          <div className="h-4 w-48 bg-gray-100 rounded mb-4"></div>
          <div className="h-80 bg-gray-100 rounded"></div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-[calc(100vh-4rem)] overflow-y-auto bg-gradient-to-b from-white to-accent-light/10 p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <div>
        <h2 className="text-2xl font-bold text-primary-headings mb-2">
          Organisation Overview
        </h2>
        <p className="text-sm text-primary-light mb-6">
          Overview of your organisation's performance
        </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setJobTitleModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-[#166473] text-sm font-medium rounded-md shadow-sm text-[#166473] bg-white hover:bg-[#166473]/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#166473] transition-colors duration-200"
            >
            Edit Labels
            </button>
            <button
              onClick={handleExport}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#166473] hover:bg-[#166473] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7551FF] transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Export Data
            </button>
          </div>
        </div>

        <Cards cards={cards} analyticsLoading={analyticsLoading} analytics={analytics} />
        <Charts charts={charts} chartsLoading={analyticsLoading} chartData={analytics} />
      </div>
      <UpdateJobTitleModal isOpen={jobTitleModalOpen} onClose={() => setJobTitleModalOpen(false)} />
    </div>
  );
};

export default OrganizationDashboard; 